import {
  getAuthApiUrl,
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
  shouldDebugControlPlaneSession,
} from '../../../app/services/session.service';
const ADMIN_TENANTS_API_PATH = '/api/admin/tenants';
const PLATFORM_ROOT_TENANT_ID = 'platform-root';

function buildResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getAdminApiUrl(path = ADMIN_TENANTS_API_PATH) {
  return getAuthApiUrl(path);
}

function getAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function debugAdminTenantRequest(event, details = {}) {
  if (!shouldDebugControlPlaneSession()) {
    return;
  }

  // Dev/prealpha only to inspect admin token usage and backend payloads.
  console.info('[control-plane][admin-tenants]', event, details);
}

function normalizeBackendCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

function mapApiTenant(tenant) {
  return {
    id: tenant?.id || '',
    name: tenant?.name || '',
    active: tenant?.active !== false,
    slug: tenant?.slug || '',
    planCode: tenant?.planCode || 'standard',
    enabledModuleKeys: Array.isArray(tenant?.enabledModuleKeys) ? tenant.enabledModuleKeys : [],
    settings: tenant?.settings && typeof tenant.settings === 'object' ? tenant.settings : {},
    provisioningStatus: tenant?.provisioningStatus || '',
    provisioningError: tenant?.provisioningError || '',
    databaseName: tenant?.databaseName || '',
    ownerEmail: tenant?.ownerEmail || '',
    ownerInviteStatus: tenant?.ownerInviteStatus || '',
    ownerInviteToken: tenant?.ownerInviteToken || '',
    ownerInviteUrl: tenant?.ownerInviteUrl || '',
    ownerInvitePreviewToken: tenant?.ownerInvitePreviewToken || '',
    migrationStatus: tenant?.migrationStatus || '',
    migrationCutoverPending: tenant?.migrationCutoverPending === true,
    ownerInviteExpiresAt: tenant?.ownerInviteExpiresAt || null,
    databaseHost: tenant?.databaseHost || '',
    databasePort: tenant?.databasePort ?? null,
    databaseUsername: tenant?.databaseUsername || '',
    userCount: Number.isFinite(tenant?.userCount) ? tenant.userCount : null,
    migrationError: tenant?.migrationError || '',
    createdAt: tenant?.createdAt || null,
    updatedAt: tenant?.updatedAt || null,
    isPlatformRoot: tenant?.id === PLATFORM_ROOT_TENANT_ID,
  };
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function mapTenantMutationError(response, fallbackError, fallbackMessage, knownPayload = undefined) {
  const payload = knownPayload === undefined ? await parseJson(response) : knownPayload;
  const backendMessage =
    payload && typeof payload === 'object'
      ? payload.message || payload.error || ''
      : '';
  const backendCode = normalizeBackendCode(payload?.error || payload?.code || payload?.reason);
  const normalizedMessage = normalizeBackendCode(backendMessage);
  debugAdminTenantRequest('mutation-error', {
    status: response.status,
    backendError: payload?.error || '',
    backendMessage,
  });

  if (response.status === 405 || response.status === 501 || backendCode === 'unsupported') {
    return buildResult({
      ok: false,
      error: 'UNSUPPORTED',
      message: backendMessage || 'Eliminazione tenant non ancora supportata dal backend.',
    });
  }

  if (response.status === 400) {
    if (
      backendCode === 'platform_root_protected' ||
      normalizedMessage.includes('platform_root_tenant_cannot_be_deleted')
    ) {
      return buildResult({
        ok: false,
        error: 'PLATFORM_ROOT_PROTECTED',
        message: 'Il tenant platform-root non puo essere eliminato.',
      });
    }

    return buildResult({
      ok: false,
      error: 'TENANT_VALIDATION_FAILED',
      message: backendMessage || 'Dati tenant non validi.',
    });
  }

  if (response.status === 401 && isSessionRefreshRequiredPayload(payload)) {
    return buildResult({
      ok: false,
      error: 'SESSION_REFRESH_REQUIRED',
      message: payload?.message || 'Sessione non aggiornata. Effettua di nuovo l\'accesso.',
    });
  }

  if (response.status === 401 && isSessionExpiredPayload(payload)) {
    return buildResult({
      ok: false,
      error: 'SESSION_EXPIRED',
      message: payload?.message || 'Sessione scaduta. Effettua di nuovo il login.',
    });
  }

  if (response.status === 403) {
    return buildResult({
      ok: false,
      error:
        payload?.error === 'workspace_access_not_allowed'
          ? 'WORKSPACE_ACCESS_NOT_ALLOWED'
          : 'ADMIN_FORBIDDEN',
      message: backendMessage || 'Accesso admin non consentito per la sessione corrente.',
    });
  }

  if (response.status === 404) {
    return buildResult({
      ok: false,
      error: 'TENANT_NOT_FOUND',
      message: backendMessage || 'Tenant non trovato.',
    });
  }

  if (response.status === 409) {
    if (backendCode === 'tenant_active' || backendCode === 'active_tenant_delete_not_allowed') {
      return buildResult({
        ok: false,
        error: 'TENANT_ACTIVE',
        message: 'Disattiva il tenant prima di eliminarlo.',
      });
    }

    if (backendCode === 'tenant_operation_in_progress') {
      return buildResult({
        ok: false,
        error: 'TENANT_OPERATION_IN_PROGRESS',
        message:
          'Il tenant ha un provisioning o una migrazione in corso. Riprova quando l\'operazione e conclusa.',
      });
    }

    return buildResult({
      ok: false,
      error: 'TENANT_CONFLICT',
      message: backendMessage || 'Tenant, slug o email owner già esistenti.',
    });
  }

  return buildResult({
    ok: false,
    error: fallbackError,
    message: backendMessage || fallbackMessage,
  });
}

export async function listAdminTenants() {
  try {
    const token = getStoredSessionToken();
    const response = await fetch(getAdminApiUrl(), {
      headers: getAuthHeaders(),
    });
    const payload = await parseJson(response);
    debugAdminTenantRequest('list-response', {
      status: response.status,
      ok: response.ok,
      backendError: payload?.error || '',
      tokenPreview: token ? `${token.slice(0, 12)}...` : '',
    });

    if (response.status === 401 && isSessionRefreshRequiredPayload(payload)) {
      return buildResult({
        ok: false,
        error: 'SESSION_REFRESH_REQUIRED',
        message: payload?.message || 'Sessione non aggiornata. Effettua di nuovo l\'accesso.',
      });
    }

    if (response.status === 401 && isSessionExpiredPayload(payload)) {
      return buildResult({
        ok: false,
        error: 'SESSION_EXPIRED',
        message: payload?.message || 'Sessione scaduta. Effettua di nuovo il login.',
      });
    }

    if (response.status === 403) {
      return buildResult({
        ok: false,
        error:
          payload?.error === 'workspace_access_not_allowed'
            ? 'WORKSPACE_ACCESS_NOT_ALLOWED'
            : payload?.error === 'platform_admin_required'
              ? 'PLATFORM_ADMIN_REQUIRED'
              : 'ADMIN_FORBIDDEN',
        message: payload?.message || 'Accesso admin non consentito per la sessione corrente.',
      });
    }

    if (!response.ok) {
      throw new Error(`ADMIN_TENANTS_LIST_FAILED_${response.status}`);
    }

    if (!Array.isArray(payload)) {
      throw new Error('ADMIN_TENANTS_INVALID_PAYLOAD');
    }

    return buildResult({
      ok: true,
      data: {
        tenants: payload.map(mapApiTenant),
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANTS_UNAVAILABLE',
      message: 'Backend admin tenant non disponibile.',
    });
  }
}

export async function updateAdminTenantStatus(tenantId, active) {
  try {
    const response = await fetch(getAdminApiUrl(`${ADMIN_TENANTS_API_PATH}/${tenantId}/status`), {
      method: 'PATCH',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ active }),
    });
    if (!response.ok) {
      return await mapTenantMutationError(
        response,
        'ADMIN_TENANT_STATUS_FAILED',
        'Aggiornamento tenant non completato.'
      );
    }

    const payload = await parseJson(response);

    return buildResult({
      ok: true,
      data: {
        tenant: mapApiTenant(payload),
      },
      message: active ? 'Tenant attivato.' : 'Tenant disattivato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANT_STATUS_UNAVAILABLE',
      message: 'Aggiornamento tenant non completato.',
    });
  }
}

export async function createAdminTenant(payload) {
  try {
    const token = getStoredSessionToken();
    const response = await fetch(getAdminApiUrl(), {
      method: 'POST',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    const responsePayload = await parseJson(response);
    debugAdminTenantRequest('create-response', {
      status: response.status,
      ok: response.ok,
      backendError: responsePayload?.error || '',
      backendMessage: responsePayload?.message || '',
      tokenPreview: token ? `${token.slice(0, 12)}...` : '',
      tenantId: payload?.tenantId || '',
    });

    if (!response.ok) {
      return await mapTenantMutationError(
        response,
        'ADMIN_TENANT_CREATE_FAILED',
        'Creazione tenant non completata.',
        responsePayload
      );
    }

    return buildResult({
      ok: true,
      data: {
        tenant: mapApiTenant(responsePayload),
      },
      message: 'Tenant creato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANT_CREATE_UNAVAILABLE',
      message: 'Backend admin tenant non disponibile.',
    });
  }
}

export async function getAdminTenant(tenantId) {
  try {
    const response = await fetch(getAdminApiUrl(`${ADMIN_TENANTS_API_PATH}/${tenantId}`), {
      headers: getAuthHeaders(),
    });
    const payload = await parseJson(response);

    if (!response.ok) {
      return await mapTenantMutationError(
        response,
        'ADMIN_TENANT_DETAIL_FAILED',
        'Lettura tenant non completata.',
        payload
      );
    }

    return buildResult({
      ok: true,
      data: {
        tenant: mapApiTenant(payload),
      },
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANT_DETAIL_UNAVAILABLE',
      message: 'Backend admin tenant non disponibile.',
    });
  }
}

export async function updateAdminTenant(tenantId, payload) {
  try {
    const response = await fetch(getAdminApiUrl(`${ADMIN_TENANTS_API_PATH}/${tenantId}`), {
      method: 'PATCH',
      headers: getAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(payload),
    });
    const responsePayload = await parseJson(response);

    if (!response.ok) {
      return await mapTenantMutationError(
        response,
        'ADMIN_TENANT_UPDATE_FAILED',
        'Aggiornamento tenant non completato.',
        responsePayload
      );
    }

    return buildResult({
      ok: true,
      data: {
        tenant: mapApiTenant(responsePayload),
      },
      message: 'Tenant aggiornato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANT_UPDATE_UNAVAILABLE',
      message: 'Backend admin tenant non disponibile.',
    });
  }
}

export async function deleteAdminTenant(tenantId) {
  try {
    const response = await fetch(getAdminApiUrl(`${ADMIN_TENANTS_API_PATH}/${tenantId}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      return await mapTenantMutationError(
        response,
        'ADMIN_TENANT_DELETE_FAILED',
        'Eliminazione tenant non completata.'
      );
    }

    return buildResult({
      ok: true,
      message: 'Tenant eliminato.',
    });
  } catch {
    return buildResult({
      ok: false,
      error: 'ADMIN_TENANT_DELETE_UNAVAILABLE',
      message: 'Backend admin tenant non disponibile.',
    });
  }
}
